import katex from 'katex';
import DOMPurify from 'dompurify';
import { LatexText } from './LatexText';
import { INLINE_DOLLAR_SOURCE } from './mathDelimiters';
import 'katex/dist/katex.min.css';
import './editor/rich-editor.css';

interface RichContentProps {
  children: string;
  className?: string;
}

// KaTeX output uses these classes/attributes — allow them through DOMPurify
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PURIFY_CONFIG: any = {
  // Allow standard formatting + KaTeX markup + media embeds
  ADD_TAGS: ['math', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'mspace',
             'mtext', 'mover', 'munder', 'msqrt', 'mtable', 'mtr', 'mtd', 'annotation',
             'semantics', 'svg', 'path', 'use', 'defs', 'g', 'rect', 'line',
             'iframe', 'video', 'audio'],
  ADD_ATTR: ['class', 'style', 'src', 'allowfullscreen', 'frameborder',
             'aria-hidden', 'focusable', 'role', 'viewBox', 'xmlns',
             'xmlns:xlink', 'xlink:href', 'href', 'd', 'fill', 'stroke',
             'width', 'height', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
             'transform', 'preserveAspectRatio', 'data-latex', 'data-type',
             'controls', 'preload'],
  // Block javascript: URIs and event handlers
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur',
                'onkeydown', 'onkeyup', 'onkeypress', 'onsubmit', 'onchange',
                'oninput', 'onpaste', 'ondrop', 'ondragover'],
  FORCE_BODY: false,
};

// Same currency-safe rule as LatexText, additionally never spanning HTML tags.
const INLINE_DOLLAR_RE = new RegExp(INLINE_DOLLAR_SOURCE.replace('[^$\\n\\\\]', '[^$\\n\\\\<>]'), 'g');

/**
 * Pre-processes LaTeX delimiters inside an HTML string, replacing them with
 * KaTeX-rendered HTML so they display correctly via dangerouslySetInnerHTML.
 * Then sanitizes with DOMPurify to prevent XSS.
 */
function renderLatexInHtml(html: string): string {
  // Display math: \[...\]
  let result = html.replace(/[\\]\[[\s\S]+?[\\]\]/g, (match) => {
    const content = match.slice(2, -2).trim();
    return katex.renderToString(content, { throwOnError: false, output: 'html', displayMode: true });
  });
  // Inline math: \(...\)
  result = result.replace(/[\\]\([\s\S]+?[\\]\)/g, (match) => {
    const content = match.slice(2, -2).trim();
    return katex.renderToString(content, { throwOnError: false, output: 'html', displayMode: false });
  });
  // Display math: $$...$$
  result = result.replace(/\$\$[\s\S]+?\$\$/g, (match) => {
    const content = match.slice(2, -2).trim();
    return katex.renderToString(content, { throwOnError: false, output: 'html', displayMode: true });
  });
  // Inline math: $...$
  result = result.replace(INLINE_DOLLAR_RE, (match) =>
    katex.renderToString(match.slice(1, -1).trim(), { throwOnError: false, output: 'html', displayMode: false })
  );
  // Sanitize before returning — removes script tags, event handlers, javascript: URIs
  return String(DOMPurify.sanitize(result, PURIFY_CONFIG));
}

/**
 * Returns a plain-text summary of rich content suitable for list previews.
 * Replaces media embeds with readable placeholders before stripping tags.
 */
export function getRichPreviewText(content: string): string {
  if (!content) return '';

  // Decode HTML entities so stored-as-encoded content is handled correctly
  const decoded = content
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  if (!decoded.trimStart().startsWith('<')) return decoded;

  return decoded
    .replace(/<iframe[^>]*>/gi, '[Video] ')
    .replace(/<video[^>]*>/gi, '[Video] ')
    .replace(/<audio[^>]*>/gi, '[Audio] ')
    .replace(/<img[^>]*>/gi, '[Image] ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Renders either HTML rich content (from the TipTap editor) or legacy plain LaTeX strings.
 * HTML is detected by checking if the string starts with '<' (after entity decoding).
 */
export function RichContent({ children, className }: RichContentProps) {
  if (!children) return null;

  const decoded = children
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  if (decoded.trimStart().startsWith('<')) {
    return (
      <div
        className={`rich-content prose prose-sm max-w-none ${className ?? ''}`}
        dangerouslySetInnerHTML={{ __html: renderLatexInHtml(decoded) }}
      />
    );
  }

  return <LatexText className={className}>{decoded}</LatexText>;
}
